/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { businessService } from "@src/@api-services/business";
import type { BusinessComplianceHistory, BusinessFlag } from "../types";
import { VERIFICATION_STATUS } from "../constants";
import { type SortOrderValue } from "@src/shared/constants/sort-order.constants";

// ** Types
export interface Business {
  id: string | number;
  businessName: string;
  email: string;
  phone: string;
  verificationStatus: (typeof VERIFICATION_STATUS)[keyof typeof VERIFICATION_STATUS];
  isActive: number;
  description: string;
  website: string;
  createdAt: string;
  updatedAt: string;
  [key: string]: any;
}

interface GetBusinessesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  city?: string;
  category?: string;
  orderBy?: string;
  orderDirection?: SortOrderValue;
  [key: string]: any;
}

interface BusinessesState {
  data: Business[];
  total: number;
  params: GetBusinessesParams;
  selectedBusiness: Business | null;
  businessFlags: BusinessFlag[];
  complianceHistory: BusinessComplianceHistory | null;
  listLoading: boolean;
  detailLoading: boolean;
  complianceLoading: boolean;
  saving: boolean;
  error: string | null;
}

// ** Async Thunks
export const getBusinesses = createAsyncThunk(
  "businesses/getBusinesses",
  async (params: GetBusinessesParams, { rejectWithValue }) => {
    try {
      const response = await businessService.getBusinesses(params);
      return {
        params,
        data: response.data.data.data,
        total: response.data.data.totalRecord,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch businesses",
      );
    }
  },
);

export const getBusiness = createAsyncThunk(
  "businesses/getBusiness",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await businessService.getBusiness(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch business",
      );
    }
  },
);

export const getBusinessCompliance = createAsyncThunk(
  "businesses/getBusinessCompliance",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const [flagsResponse, historyResponse] = await Promise.all([
        businessService.getBusinessFlags(id),
        businessService.getBusinessComplianceHistory(id),
      ]);

      return {
        flags: (flagsResponse.data.data || []) as BusinessFlag[],
        history: historyResponse.data.data as BusinessComplianceHistory,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch compliance data",
      );
    }
  },
);

export const updateBusinessStatus = createAsyncThunk(
  "businesses/updateStatus",
  async (
    {
      id,
      status,
      verificationNotes,
    }: { id: string | number; status: string; verificationNotes?: string },
    { dispatch, getState, rejectWithValue }: any,
  ) => {
    try {
      const response = await businessService.updateBusinessStatus(id, {
        status,
        verificationNotes,
      });
      const state = getState();
      await dispatch(getBusinesses(state.businesses.params));
      await dispatch(getBusinessCompliance(id));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update business status",
      );
    }
  },
);

export const flagBusinessAction = createAsyncThunk(
  "businesses/flagBusiness",
  async (
    {
      id,
      reason,
      comment,
    }: { id: string | number; reason: string; comment?: string },
    { dispatch, rejectWithValue }: any,
  ) => {
    try {
      const response = await businessService.flagBusiness(id, {
        reason,
        comment,
      });
      await dispatch(getBusinessCompliance(id));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to flag business",
      );
    }
  },
);

export const addBusinessCommentAction = createAsyncThunk(
  "businesses/addBusinessComment",
  async (
    { id, comment }: { id: string | number; comment: string },
    { dispatch, rejectWithValue }: any,
  ) => {
    try {
      const response = await businessService.addBusinessInternalComment(
        id,
        comment,
      );
      await dispatch(getBusinessCompliance(id));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add comment",
      );
    }
  },
);

const initialState: BusinessesState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "id",
    orderDirection: "ASC",
  },
  selectedBusiness: null,
  businessFlags: [],
  complianceHistory: null,
  listLoading: false,
  detailLoading: false,
  complianceLoading: false,
  saving: false,
  error: null,
};

export const businessesSlice = createSlice({
  name: "businesses",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedBusiness: (state) => {
      state.selectedBusiness = null;
      state.businessFlags = [];
      state.complianceHistory = null;
    },
    setParams: (state, action) => {
      state.params = { ...state.params, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBusinesses.pending, (state) => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(getBusinesses.fulfilled, (state, action) => {
        state.listLoading = false;
        state.data = action.payload.data;
        state.params = action.payload.params;
        state.total = action.payload.total;
      })
      .addCase(getBusinesses.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getBusiness.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(getBusiness.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedBusiness = action.payload;
      })
      .addCase(getBusiness.rejected, (state) => {
        state.detailLoading = false;
        state.selectedBusiness = null;
      })
      .addCase(getBusinessCompliance.pending, (state) => {
        state.complianceLoading = true;
      })
      .addCase(getBusinessCompliance.fulfilled, (state, action) => {
        state.complianceLoading = false;
        state.businessFlags = action.payload.flags;
        state.complianceHistory = action.payload.history;
      })
      .addCase(getBusinessCompliance.rejected, (state) => {
        state.complianceLoading = false;
      })
      .addCase(updateBusinessStatus.fulfilled, (state, action) => {
        const { id, status, verificationNotes } = action.meta.arg;

        if (
          state.selectedBusiness &&
          String(state.selectedBusiness.id) === String(id)
        ) {
          state.selectedBusiness.verificationStatus = status as any;
          if (status === VERIFICATION_STATUS.REJECTED && verificationNotes) {
            state.selectedBusiness.verificationNotes = verificationNotes;
          }
        }

        const index = state.data.findIndex((b) => String(b.id) === String(id));
        if (index !== -1) {
          state.data[index].verificationStatus = status as any;
          if (status === VERIFICATION_STATUS.REJECTED && verificationNotes) {
            state.data[index].verificationNotes = verificationNotes;
          }
        }
      })
      .addMatcher(
        (action) =>
          action.type.endsWith("/pending") &&
          [
            "flagBusinessAction",
            "addBusinessCommentAction",
            "updateBusinessStatus",
          ].some((type) => action.type.includes(type)),
        (state) => {
          state.saving = true;
          state.error = null;
        },
      )
      .addMatcher(
        (action) =>
          action.type.endsWith("/fulfilled") &&
          [
            "flagBusinessAction",
            "addBusinessCommentAction",
            "updateBusinessStatus",
          ].some((type) => action.type.includes(type)),
        (state) => {
          state.saving = false;
        },
      )
      .addMatcher(
        (action) =>
          action.type.endsWith("/rejected") &&
          [
            "flagBusinessAction",
            "addBusinessCommentAction",
            "updateBusinessStatus",
          ].some((type) => action.type.includes(type)),
        (state, action: any) => {
          state.saving = false;
          state.error = action?.payload as string;
        },
      );
  },
});

export const { clearError, clearSelectedBusiness, setParams } =
  businessesSlice.actions;

export default businessesSlice.reducer;
